import { NavLink, Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Shield, Menu, X, LogOut, Radar } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/coverage", label: "Global Coverage" },
  { to: "/track", label: "Track Shipment" },
  { to: "/contact", label: "Request Quote" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 backdrop-blur-xl bg-[#060C1F]/85 border-b border-[#A1AEC2]/15"
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
          <div className="relative w-10 h-10 flex items-center justify-center border border-[#D4AF37]/60 group-hover:border-[#D4AF37]">
            <Shield className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#D4AF37] aegis-pulse" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-black text-[#F0F4F8] text-lg tracking-widest">A&amp;P<span className="text-[#D4AF37]">·</span>FAST TRACK</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#9EABC0]">Offshore &amp; High-Value Logistics</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-[11px] uppercase tracking-[0.22em] hover:text-[#D4AF37] ${
                  isActive ? "text-[#D4AF37]" : "text-[#9EABC0]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard"
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] flex items-center gap-2 border border-[#D4AF37]/50 px-4 py-2 hover:bg-[#D4AF37]/10"
              >
                <Radar className="w-4 h-4" /> Control Room
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#9EABC0] hover:text-[#F0F4F8] flex items-center gap-2"
              >
                <LogOut className="w-4 h-4" /> Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#060C1F] bg-[#D4AF37] hover:bg-[#C5A059] px-5 py-3"
            >
              Admin Access
            </Link>
          )}
        </div>

        <button
          className="lg:hidden text-[#F0F4F8]"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#A1AEC2]/10 bg-[#060C1F]" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `font-mono text-xs uppercase tracking-[0.22em] ${isActive ? "text-[#D4AF37]" : "text-[#9EABC0]"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Control Room</Link>
                <button onClick={handleLogout} className="text-left font-mono text-xs uppercase tracking-[0.22em] text-[#9EABC0]">Logout</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="font-mono text-xs uppercase tracking-[0.22em] text-[#D4AF37]">Admin Access</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
